import type { FC, ReactNode } from 'react'
import type { ProjectDetail } from '@/types'

import Link from 'next/link'
import { Reveal } from '@/components/motion/reveal'
import { ParallaxImage } from '@/components/motion/parallax-image'
import { ArrowLink } from '@/components/site/arrow-link'
import { SectionHeading } from '@/components/site/section-heading'

interface WorkFocusProps {
    project: ProjectDetail
}

export const WorkFocus: FC<WorkFocusProps> = ({ project }): ReactNode => {
    const href = `/work/${project.slug}`

    return (
        <section
            id='in-focus'
            className='relative z-[2] flex w-full flex-col gap-10 overflow-clip bg-background px-4 pb-[240px] lg:gap-12 lg:px-6'
        >
            <div className='flex w-full flex-col md:flex-row md:items-end md:justify-between md:gap-6'>
                <div className='w-full md:flex-1'>
                    <SectionHeading lead="Close" title="up" />
                </div>

                <div className='flex flex-col items-start gap-2 pb-6 text-foreground md:items-end'>
                    <p
                        data-mdlfy-cms={`projects:${project.id}:client`}
                        className='t-italic text-left md:text-right'
                    >
                        {project.client}
                    </p>

                    {project.category && (
                        <p
                            data-mdlfy-cms={`categories:${project.category.id}:name`}
                            className='t-caps text-left text-muted-foreground md:text-right'
                        >
                            {project.category.name}
                        </p>
                    )}
                </div>
            </div>

            <div className='grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] lg:gap-12'>
                <Reveal y={60}>
                    <Link
                        href={href}
                        aria-label={`Open the ${project.client} case study`}
                        className='group/focus relative block aspect-[1.41562] w-full overflow-hidden bg-muted'
                    >
                        {project.cover && (
                            <ParallaxImage
                                src={project.cover}
                                alt={project.coverAlt ?? ''}
                                className='absolute inset-0 transition-transform duration-[1200ms] ease-studio group-hover/focus:scale-[1.03]'
                            />
                        )}

                        <span
                            aria-hidden='true'
                            className='pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent opacity-0 transition-opacity duration-700 ease-studio group-hover/focus:opacity-100'
                        />

                        <span className='absolute inset-x-10 bottom-[-109px] flex justify-center transition-[bottom] duration-700 ease-studio group-hover/focus:bottom-[19px]'>
                            <span className='t-title text-center text-cream'>
                                View project
                            </span>
                        </span>
                    </Link>
                </Reveal>

                <div className='flex flex-col justify-between gap-12'>
                    <div className='grid grid-cols-2 gap-6'>
                        {project.imageOne && (
                            <Reveal y={40} delay={0.08}>
                                <ParallaxImage
                                    src={project.imageOne}
                                    alt={project.imageOneAlt ?? ''}
                                    className='aspect-[0.8] w-full'
                                />
                            </Reveal>
                        )}

                        {project.imageTwo && (
                            <Reveal y={40} delay={0.16} className='pt-16'>
                                <ParallaxImage
                                    src={project.imageTwo}
                                    alt={project.imageTwoAlt ?? ''}
                                    className='aspect-[0.8] w-full'
                                />
                            </Reveal>
                        )}
                    </div>

                    <div className='flex flex-col gap-6 text-foreground'>
                        <h3
                            data-mdlfy-cms={`projects:${project.id}:title`}
                            className='t-name text-left'
                        >
                            {project.title}
                        </h3>

                        {project.summary && (
                            <p
                                data-mdlfy-cms={`projects:${project.id}:summary`}
                                className='t-body max-w-[520px] text-left text-muted-foreground'
                            >
                                {project.summary}
                            </p>
                        )}

                        <dl className='flex flex-col gap-2'>
                            <div className='flex items-center gap-6'>
                                <dt className='t-italic max-w-[32%] flex-1 text-left'>
                                    Year
                                </dt>

                                <dd
                                    data-mdlfy-cms={`projects:${project.id}:year`}
                                    className='t-body flex-1 text-left'
                                >
                                    {project.year}
                                </dd>
                            </div>
                        </dl>

                        <div>
                            <ArrowLink href={href} label="Open the project" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
